import { useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Spinner } from '../common/Spinner';
import { useProjectTimeOffCoverage } from '../../hooks/useTimeOffCoverage';
import { formatDisplayDate } from '../../utils/format';
import { ENGLISH_LABEL_FORMAT, formatMonthYear } from '../../utils/dateFormat';

/**
 * MKS-361 — the PM's "Out this month" panel.
 *
 * One month at a time, with arrows, because that is the horizon a PM plans
 * on: "who is away in October, and who is covering them here". It shows the
 * project's own cover, never the requester's other projects — a PM looking at
 * Acme does not need to know who stands in for the same person on Beta.
 *
 * Absences only (the endpoint filters presence types out): a teammate on home
 * office is not "out", and listing them would bury the people who are.
 */

/** First and last day of a month, as `YYYY-MM-DD`. */
export function monthBounds(year: number, month: number): { from: string; to: string } {
  const pad = (n: number) => String(n).padStart(2, '0');
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return {
    from: `${year}-${pad(month + 1)}-01`,
    to: `${year}-${pad(month + 1)}-${pad(lastDay)}`,
  };
}

interface ProjectCoveragePanelProps {
  projectId: number;
}

export default function ProjectCoveragePanel({ projectId }: ProjectCoveragePanelProps) {
  const today = new Date();
  const [offset, setOffset] = useState(0);

  const { year, month } = useMemo(() => {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + offset, 1));
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
    // `today` is recreated every render; the month only depends on the offset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  const { from, to } = monthBounds(year, month);
  const { data: entries, isLoading, error } = useProjectTimeOffCoverage(projectId, from, to, true);

  // English in both release-flag branches (MKS-351): this heading already read
  // `October 2026` before the ticket, so the flag must not touch it.
  const monthLabel = formatMonthYear(from, ENGLISH_LABEL_FORMAT);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Out this month</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Previous month"
            onClick={() => setOffset((o) => o - 1)}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <span className="text-sm font-medium text-gray-700 w-36 text-center">{monthLabel}</span>
          <button
            type="button"
            aria-label="Next month"
            onClick={() => setOffset((o) => o + 1)}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <p className="text-sm text-red-600">
          Failed to load coverage: {(error as Error).message}
        </p>
      ) : !entries || entries.length === 0 ? (
        <p className="text-sm text-gray-500">Nobody on this project is away in {monthLabel}.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {entries.map((entry) => (
            <li key={entry.timeOffId} className="py-3 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: entry.typeColour }}
                  />
                  <span className="text-sm font-medium text-gray-900">{entry.userName}</span>
                  {entry.state === 'submitted' && (
                    <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                      not approved yet
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {entry.typeName ?? 'Time off'} · {formatDisplayDate(entry.dateFrom)} –{' '}
                  {formatDisplayDate(entry.dateTo)}
                </div>
              </div>
              <div className="text-sm text-right">
                {entry.substitute ? (
                  <>
                    <div className="text-gray-900">Covered by {entry.substitute.name}</div>
                    {entry.substitute.note && (
                      <div className="text-gray-500">{entry.substitute.note}</div>
                    )}
                  </>
                ) : (
                  <span className="text-amber-700">No substitute</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
